(async () => {
    const { Core } = await importModule('Core');
    const { Swiper } = await importModule('Swiper');

    const NAV_ARROWS = 'arrows';
    const NAV_PAG = 'pagination';
    const CN_AUTOPLAY = '@autoplay';
    const SP_AUTOPLAY = '--autoplay';
    const ATTR_AUTOPLAY = 'autoplay';
    const CN_NAV_DISABLED = '!disabled';
    const SEL_BG_VIDEO = 'background-video';
    const SEL_VIDEO_SLIDE = '[data-video-slide]';
    const ATTR_HAS_VIDEO = 'has-video';
    const ATTR_NAVIGATION = 'navigation';
    const ATTR_AUTO_HEIGHT = 'auto-height';
    
    customElements.define('slideshow-hero', class extends Core {
        elements = {
            swiper: '[data-swiper]',
            next: '[data-next]',
            prev: '[data-prev]'
        }

        render() {
            this._setSwiperProps();
            this._initSwiper();
            this._initAutoplay();
            this._initAmbientEdges();
        }
        
        _initSwiper() {
            this.swiper = new Swiper(this.$swiper, this.swiperProps);
            this.hasVideo && this.swiper.on('slideChange', this._toggleVideo.bind(this));
        }

        _initAutoplay() {
            if(this.autoplay) {
                this.classList.add(CN_AUTOPLAY);
                this.style.setProperty(SP_AUTOPLAY, `${this.autoplay}ms`);
                this.swiper.on('autoplayStop', () => {
                    this.classList.remove(CN_AUTOPLAY);
                });
            }
        }

        _initAmbientEdges() {
            this._syncAmbientEdges = this._syncAmbientEdges.bind(this);
            this._syncAmbientEdges();
            this.swiper.on('slideChange', this._syncAmbientEdges);
            this.swiper.on('transitionEnd', this._syncAmbientEdges);
            this.swiper.on('resize', this._syncAmbientEdges);
            window.addEventListener('resize', this._syncAmbientEdges, { passive: true });
            this.querySelectorAll('.\\#media-image, .\\#media-image-mobile').forEach(image => {
                if(image.complete) return;
                image.addEventListener('load', this._syncAmbientEdges, { once: true });
            });
        }

        _syncAmbientEdges() {
            this.querySelectorAll('.\\#media-image-wrapper').forEach(wrapper => {
                const image = Array.from(wrapper.querySelectorAll('.\\#media-image, .\\#media-image-mobile'))
                    .find(item => getComputedStyle(item).display !== 'none');
                if(!image || !image.naturalWidth || !image.naturalHeight) return;

                const wrapperWidth = wrapper.clientWidth;
                const wrapperHeight = wrapper.clientHeight;
                if(!wrapperWidth || !wrapperHeight) return;

                const imageRatio = image.naturalWidth / image.naturalHeight;
                const wrapperRatio = wrapperWidth / wrapperHeight;
                let renderedWidth = wrapperWidth;
                let renderedHeight = wrapperHeight;

                if(imageRatio > wrapperRatio) {
                    renderedHeight = wrapperWidth / imageRatio;
                } else {
                    renderedWidth = wrapperHeight * imageRatio;
                }

                const renderedLeft = (wrapperWidth - renderedWidth) / 2;
                const renderedTop = (wrapperHeight - renderedHeight) / 2;
                const ambientFadeDistance = Math.round(renderedWidth * 0.065);
                const ambientFadeNear = Math.round(ambientFadeDistance * 0.25);
                const ambientFadeMid = Math.round(ambientFadeDistance * 0.55);

                wrapper.style.setProperty('--ambient-image-width', `${renderedWidth}px`);
                wrapper.style.setProperty('--ambient-image-height', `${renderedHeight}px`);
                wrapper.style.setProperty('--ambient-image-top', `${renderedTop}px`);
                wrapper.style.setProperty('--ambient-fade-distance', `${ambientFadeDistance}px`);
                wrapper.style.setProperty('--ambient-fade-near', `${ambientFadeNear}px`);
                wrapper.style.setProperty('--ambient-fade-mid', `${ambientFadeMid}px`);
                wrapper.style.setProperty('--ambient-left-x', `${renderedLeft - renderedWidth}px`);
                wrapper.style.setProperty('--ambient-right-x', `${renderedLeft + renderedWidth}px`);
                wrapper.style.setProperty('--ambient-bleed-width', `${renderedWidth * 0.24}px`);
                wrapper.style.setProperty('--ambient-left-fade-x', `${renderedLeft - (renderedWidth * 0.24)}px`);
                wrapper.style.setProperty('--ambient-right-fade-x', `${renderedLeft + renderedWidth}px`);
                wrapper.style.setProperty('--ambient-left-gutter-width', `${renderedLeft}px`);
                wrapper.style.setProperty('--ambient-right-gutter-width', `${wrapperWidth - renderedLeft - renderedWidth}px`);
            });
        }

        _setSwiperProps() {
            this.swiperProps = {
                loop: true
            };
            
            this.swiperProps.autoHeight = this.autoHeight;

            if(this.hasVideo) 
                this.swiperProps.on = {
                    init: () => {
                        window.customElements.whenDefined(SEL_BG_VIDEO).then(() => {
                            this.videoSlides = Array.from(this.querySelectorAll(SEL_VIDEO_SLIDE)).map(video => {
                                video.querySelector(SEL_BG_VIDEO).initPlayer();
                                return video;
                            })
                        })
                    }
                }

            if(this.navigation == NAV_ARROWS)
                this.swiperProps.navigation = {
                    nextEl: this.$next,
                    prevEl: this.$prev,
                    disabledClass: CN_NAV_DISABLED
                }

            if(this.navigation == NAV_PAG) 
                this.swiperProps.pagination = {
                    el: '.swiper-pagination',
                    type: 'bullets',
                    clickable: true
                }
            
            if(this.autoplay) 
                this.swiperProps.autoplay = {
                    delay: this.autoplay
                }
        }

        _toggleVideo({ slides, activeIndex, previousIndex }) {
            const current = this.videoSlides.find((item) => item === slides[activeIndex]);
            const prev = this.videoSlides.find((item) => item === slides[previousIndex]);
            current?.querySelector(SEL_BG_VIDEO)?.play();
            prev?.querySelector(SEL_BG_VIDEO)?.pause();            
        }

        get navigation() {
            return this.getAttribute(ATTR_NAVIGATION);
        }
        get hasVideo() {
            return this.hasAttribute(ATTR_HAS_VIDEO);
        }
        get autoplay() {
            return this.getAttribute(ATTR_AUTOPLAY) && +this.getAttribute(ATTR_AUTOPLAY);
        }
        get autoHeight() {
            return this.hasAttribute(ATTR_AUTO_HEIGHT);
        }
    })
})();
